// Service worker registration + update notification.
//
// When a redeploy installs a fresh sw.js (which calls skipWaiting +
// clients.claim), the new worker takes control and fires `controllerchange`.
// Instead of force-reloading (which could discard in-progress form input),
// we show a small non-invasive banner offering a one-click reload. The old
// build keeps working meanwhile: its hashed chunks are already in the SW
// cache (cache-first), and the ErrorBoundary chunk-reload remains the safety
// net if a chunk is genuinely gone.
import {
  claimBrowserRecovery,
  classifyBrowserRecoveryError,
  clearChunkRecoveryCaches,
  markBrowserRecoveryHandled,
  notifyBrowserRecoverySuppressed,
} from "./browser-recovery";
import { forceFreshReload } from "../components/error-boundary";

let preloadErrorListenerInstalled = false;

/**
 * Vite emits this event for failed dynamic-import preloads (including CSS
 * chunks). It is not a normal window error, so the React boundary would
 * otherwise never get a chance to recover. Mark the payload so the global
 * reporter can avoid sending a duplicate event.
 */
function installVitePreloadErrorRecovery(): void {
  if (preloadErrorListenerInstalled || typeof window === "undefined") return;
  preloadErrorListenerInstalled = true;
  window.addEventListener("vite:preloadError", (event: Event) => {
    event.preventDefault();
    const viteEvent = event as Event & { payload?: unknown };
    const customEvent = event as CustomEvent<{ payload?: unknown }>;
    const error = viteEvent.payload ?? customEvent.detail?.payload ?? customEvent.detail;
    markBrowserRecoveryHandled(error);
    // A native link error can precede Vite's promise rejection for the same
    // resource. That document already has a report and recovery decision.
    if ((window as any).__uoEarlyAssetFailure) return;
    const classification = classifyBrowserRecoveryError(error, "css-preload");
    if (!classification.offline && claimBrowserRecovery()) {
      void clearChunkRecoveryCaches().finally(() => forceFreshReload(true));
      return;
    }
    notifyBrowserRecoverySuppressed(classification.offline);
  });
}

export function setupServiceWorker(): void {
  installVitePreloadErrorRecovery();
  if (!('serviceWorker' in navigator)) return;

  let notified = false;
  const hadController = !!navigator.serviceWorker.controller;

  const notify = (registration?: ServiceWorkerRegistration) => {
    // Never notify on the very first install (no prior controller).
    if (notified || !hadController) return;
    notified = true;
    showUpdateBanner(registration);
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => notify());

  window.addEventListener('load', () => {
    // `updateViaCache: 'none'` makes the browser fetch /sw.js from the network
    // (never the HTTP cache) on every update check, so a new service worker is
    // picked up promptly after a redeploy instead of being pinned to a stale one.
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        console.log('SW registered:', registration.scope);

        // Proactive banner: show it as soon as the NEW worker finishes
        // installing (state 'installed'), i.e. before it takes control. This
        // way the user is invited to reload BEFORE navigating to a lazy route
        // whose old chunk hashes may already be gone from the server.
        const watchInstalling = (worker: ServiceWorker | null) => {
          if (!worker) return;
          worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
              notify(registration);
            }
          });
        };
        // A worker may already be waiting (update found while the tab was closed).
        if (registration.waiting && navigator.serviceWorker.controller) {
          notify(registration);
        }
        watchInstalling(registration.installing);
        registration.addEventListener('updatefound', () => {
          watchInstalling(registration.installing);
        });

        // Proactively check for a newer worker on load and periodically, so
        // long-lived tabs still pick up a redeploy without a manual refresh.
        registration.update().catch(() => {});
        setInterval(() => registration.update().catch(() => {}), 30 * 60 * 1000);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// Vanilla-DOM banner so it works regardless of React mount/hydration state.
function showUpdateBanner(registration?: ServiceWorkerRegistration): void {
  if (document.getElementById('sw-update-banner')) return;

  const banner = document.createElement('div');
  banner.id = 'sw-update-banner';
  banner.setAttribute('role', 'status');
  banner.setAttribute('data-testid', 'banner-sw-update');
  banner.style.cssText = [
    'position:fixed',
    'bottom:16px',
    'left:50%',
    'transform:translateX(-50%)',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'gap:12px',
    'padding:10px 14px',
    'border-radius:8px',
    'background:#1c1c1e',
    'color:#f5f5f4',
    'border:1px solid #3a3a3c',
    'box-shadow:0 4px 16px rgba(0,0,0,0.35)',
    'font-family:Montserrat,system-ui,sans-serif',
    'font-size:14px',
    'line-height:1.4',
    'max-width:calc(100vw - 32px)',
  ].join(';');

  const text = document.createElement('span');
  text.textContent = 'Nuova versione disponibile — Aggiorna ora';

  const reloadBtn = document.createElement('button');
  reloadBtn.type = 'button';
  reloadBtn.textContent = 'Ricarica';
  reloadBtn.setAttribute('data-testid', 'button-sw-update-reload');
  reloadBtn.style.cssText =
    'background:#c8a44d;color:#1c1c1e;border:none;border-radius:6px;padding:6px 12px;font-weight:600;font-size:13px;cursor:pointer;font-family:inherit';
  reloadBtn.addEventListener('click', () => {
    // If the new worker is still waiting, promote it first (SKIP_WAITING →
    // skipWaiting() → activate retains one prior app cache), then reload so
    // the fresh index.html + chunks are fetched. If activation takes longer than
    // a beat (or the message is lost), reload anyway after a short timeout —
    // the reload itself also completes the update.
    const waiting = registration?.waiting;
    if (waiting) {
      let reloaded = false;
      const doReload = () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener('controllerchange', doReload, { once: true });
      waiting.postMessage({ type: 'SKIP_WAITING' });
      setTimeout(doReload, 800);
    } else {
      window.location.reload();
    }
  });

  const dismissBtn = document.createElement('button');
  dismissBtn.type = 'button';
  dismissBtn.textContent = '\u00d7';
  dismissBtn.setAttribute('aria-label', 'Chiudi');
  dismissBtn.setAttribute('data-testid', 'button-sw-update-dismiss');
  dismissBtn.style.cssText =
    'background:transparent;color:#a1a1aa;border:none;font-size:18px;cursor:pointer;padding:0 2px;line-height:1;font-family:inherit';
  dismissBtn.addEventListener('click', () => banner.remove());

  banner.append(text, reloadBtn, dismissBtn);

  const mount = () => document.body.appendChild(banner);
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });
}
